// relative import (not @/): this file is also the kiosk IIFE entry, and esbuild
// doesn't know vite's alias. The logger is dependency-free so the kiosk bundle
// only gains the logger itself.
import { getLogger } from "../../logger.js";

const logger = getLogger("webrtc");

export const DEFAULT_RTC_PEER_CONNECTION_CONFIG = {
  bundlePolicy: 'max-bundle',
  iceCandidatePoolSize: 8,
  iceTransportPolicy: 'all',
  iceServers: [{ urls: 'stun:stun.l.google.com:19302' }]
};

const PUSHER_COMPRESS_THRESHOLD = 9000;

export async function compressPusherPayload(payload) {
  const json = JSON.stringify(payload);
  if (json.length < PUSHER_COMPRESS_THRESHOLD) return payload;
  const stream = new Blob([json]).stream().pipeThrough(new CompressionStream('deflate'));
  const compressed = await new Response(stream).arrayBuffer();
  const base64 = btoa(String.fromCharCode(...new Uint8Array(compressed)));
  logger.debug('compressed pusher payload', { from_bytes: json.length, to_bytes: base64.length + 20 });
  return { _compressed: base64 };
}

export async function decompressPusherPayload(data) {
  if (!data._compressed) return data;
  const bytes = Uint8Array.from(atob(data._compressed), c => c.charCodeAt(0));
  const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream('deflate'));
  const json = await new Response(stream).text();
  logger.debug('decompressed pusher payload', { from_bytes: data._compressed.length + 20, to_bytes: json.length });
  return JSON.parse(json);
}

/**
 * Converts a TURN server URL to an RTCIceServer object.
 * @param {string} url
 * @returns {RTCIceServer|null}
 */
export function buildRTCIceServer(url) {
  const re = /^(turns?:)\/\/([^:]+):([^@]+)@(.+)$/
  const matches = url.match(re);
  if (!matches) {
    logger.error('invalid turn server url');
    return null;
  }
  return {
    username: decodeURIComponent(matches[2]),
    credential: decodeURIComponent(matches[3]),
    urls: `${matches[1]}${matches[4]}`,
  };
}

/**
 * Builds an RTCConfiguration by merging TURN servers into a default config.
 * @param {RTCConfiguration} defaultConfig
 * @param {string[]|null} turnServers - array of TURN server URLs
 * @returns {RTCConfiguration}
 */
export function buildRTCPeerConnectionConfig(defaultConfig, turnServers) {
  let connConfig = defaultConfig
  if (turnServers) {
    // copy the ice server list as well, otherwise the TURN entries are pushed into the caller's (shared) default config
    connConfig = { ...connConfig, iceServers: [...(connConfig.iceServers || [])] }
    for (const turnServer of turnServers) {
      const iceServer = buildRTCIceServer(turnServer)
      if (iceServer) {
        connConfig.iceServers.push(iceServer)
      }
    }
  }
  return connConfig
}

/**
 * Handles peer negotiation (perfect negotiation pattern with collision detection).
 * Used identically by both the Vue participant and the kiosk.
 *
 * @param {Object} params
 * @param {RTCPeerConnection} params.rtcPeerConnection
 * @param {RTCDataChannel} params.dataChannel
 * @param {string} params.localPeerId - this peer's id (used for tie-breaking and reply)
 * @param {string} params.remotePeerId - the remote peer's id (used for tie-breaking)
 * @param {boolean} params.makingOffer - whether this peer is currently making a negotiation offer
 * @param {RTCSessionDescriptionInit} params.sdp - the received SDP
 */
export async function handlePeerNegotiation({ rtcPeerConnection, dataChannel, localPeerId, remotePeerId, makingOffer, sdp }) {
  const offerCollision = (sdp.type === 'offer') &&
    (makingOffer || rtcPeerConnection.signalingState !== 'stable');

  // tie-breaker based on peer id to avoid issue with simultaneous offers
  const polite = localPeerId < remotePeerId;

  const ignoreOffer = !polite && offerCollision;
  if (ignoreOffer) {
    logger.debug('ignoring negotiation offer collision', { peer_id: remotePeerId });
    return;
  }

  await rtcPeerConnection.setRemoteDescription(sdp);
  logger.debug('remote description set from negotiation offer', { peer_id: remotePeerId });

  if (sdp.type === 'offer') {
    await rtcPeerConnection.setLocalDescription();
    logger.debug('negotiation answer created', { peer_id: remotePeerId });
    try {
      dataChannel.send(JSON.stringify({
        type: "peer-negotiation",
        sdp: rtcPeerConnection.localDescription,
        fromPeerId: localPeerId,
      }));
      logger.debug('negotiation answer sent', { peer_id: remotePeerId });
    } catch (err) {
      logger.error('failed to send negotiation answer', { peer_id: remotePeerId }, err);
    }
  }
}

/**
 * Sends a negotiation offer over a data channel (outbound half of perfect negotiation).
 * The caller is responsible for managing the makingOffer flag in try/finally.
 *
 * @param {Object} params
 * @param {RTCPeerConnection} params.rtcPeerConnection
 * @param {RTCDataChannel} params.dataChannel
 * @param {string} params.localPeerId - this peer's id
 * @returns {Promise<{sent: boolean}>}
 */
export async function sendNegotiationOffer({ rtcPeerConnection, dataChannel, localPeerId }) {
  if (!dataChannel || dataChannel.readyState !== 'open') {
    logger.warn('negotiation needed but data channel not open', { peer_id: localPeerId });
    return { sent: false };
  }
  await rtcPeerConnection.setLocalDescription();
  dataChannel.send(JSON.stringify({
    type: "peer-negotiation",
    sdp: rtcPeerConnection.localDescription,
    fromPeerId: localPeerId,
  }));
  logger.debug('negotiation offer sent', { peer_id: localPeerId });
  return { sent: true };
}

/**
 * Sets the remote SDP description and adds ICE candidates.
 *
 * @param {RTCPeerConnection} rtcPeerConnection
 * @param {RTCSessionDescriptionInit} remoteSdp
 * @param {RTCIceCandidateInit[]} remoteIceCandidates
 */
export async function applyRemoteAnswer(rtcPeerConnection, remoteSdp, remoteIceCandidates) {
  await rtcPeerConnection.setRemoteDescription(remoteSdp);
  logger.debug('remote description set');
  if (remoteIceCandidates && Array.isArray(remoteIceCandidates)) {
    for (const cand of remoteIceCandidates) {
      if (cand) {
        await rtcPeerConnection.addIceCandidate(new RTCIceCandidate(cand));
        logger.debug('remote ice candidate added', { candidate: cand.candidate ? cand.candidate.substring(0, 50) + "..." : "(null)" });
      }
    }
  }
}

/**
 * Attaches diagnostic logging listeners to an RTCPeerConnection.
 *
 * @param {RTCPeerConnection} peerConn
 * @param {string} participantId - label used in log messages
 */
export function attachPeerConnectionDiagnosticListeners(peerConn, participantId) {
  peerConn.addEventListener('signalingstatechange', () => {
    logger.debug('signaling state changed', { peer_id: participantId, state: peerConn.signalingState });
  });
  peerConn.addEventListener('icecandidateerror', (errEvent) => {
    logger.warn('ice candidate error', { peer_id: participantId, code: errEvent.errorCode, text: errEvent.errorText });
  });
  peerConn.addEventListener('iceconnectionstatechange', () => {
    logger.debug('ice connection state changed', { peer_id: participantId, state: peerConn.iceConnectionState });
  });
  peerConn.addEventListener('icegatheringstatechange', () => {
    logger.debug('ice gathering state changed', { peer_id: participantId, state: peerConn.iceGatheringState });
  });
}

/**
 * Checks if a Pusher client event is targeted at a specific peer.
 * Event names may include `@memberId` suffix for targeted delivery.
 *
 * @param {string} eventName - the Pusher event name (e.g., "client-peer-sdp-answer@peer123")
 * @param {string} myPeerId - this peer's id
 * @returns {boolean} true if the event has no target or the target matches myPeerId
 */
export function isEventTargetedAtPeer(eventName, myPeerId) {
  if (eventName.includes('@')) {
    const targetMemberId = eventName.substring(eventName.lastIndexOf('@') + 1);
    if (targetMemberId !== myPeerId) {
      return false;
    }
  }
  return true;
}
